import React, { useState, useEffect } from 'react'
import moment from 'moment'
import { Calendar, ChevronDown } from 'lucide-react'
import { DayPicker } from 'react-day-picker'
import 'react-day-picker/style.css'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'

// Port of the DateRangePicker from tm2-admin, slimmed down and re-skinned
// with our ``-prefixed Tailwind. Calls `onChange(start_date, end_date)`
// (YYYY-MM-DD strings) when the user clicks Apply or picks a preset.
const DEFAULT_PRESETS = [
  { text: 'Today',        start: () => moment(),                                    end: () => moment() },
  { text: 'Yesterday',    start: () => moment().subtract(1, 'days'),                end: () => moment().subtract(1, 'days') },
  { text: 'Last 7 Days',  start: () => moment().subtract(6, 'days'),                end: () => moment() },
  { text: 'Last 30 Days', start: () => moment().subtract(29, 'days'),               end: () => moment() },
  { text: 'This Month',   start: () => moment().startOf('month'),                   end: () => moment().endOf('month') },
  { text: 'Last Month',   start: () => moment().subtract(1, 'month').startOf('month'), end: () => moment().subtract(1, 'month').endOf('month') },
]

const toDate = (value) => (value ? moment(value).toDate() : null)
const fmt    = (date)  => moment(date).format('YYYY-MM-DD')

const DateRangePicker = ({ start_date, end_date, onChange, align }) => {
  const initial = { from: toDate(start_date), to: toDate(end_date) }
  const [range, setRange] = useState(initial)
  const [tempRange, setTempRange] = useState(initial)
  const [isOpen, setIsOpen] = useState(false)

  // Re-sync when parent passes new props
  useEffect(() => {
    const next = { from: toDate(start_date), to: toDate(end_date) }
    setRange(next)
    setTempRange(next)
  }, [start_date, end_date])

  const displayText = range.from && range.to
    ? `${fmt(range.from)} – ${fmt(range.to)}`
    : 'Select date range'

  const applyRange = (next) => {
    setRange(next)
    setIsOpen(false)
    if (next.from && next.to && onChange) {
      onChange(fmt(next.from), fmt(next.to))
    }
  }

  const onPresetClick = (preset) => {
    applyRange({ from: preset.start().toDate(), to: preset.end().toDate() })
  }

  const onApply  = () => applyRange(tempRange)
  const onCancel = () => { setTempRange(range); setIsOpen(false) }

  // Dismissal (outside click / Escape) discards the pending selection, same
  // as the old hand-rolled outside-click handler.
  const onOpenChange = (open) => {
    if (!open) setTempRange(range)
    setIsOpen(open)
  }

  return (
    <Popover open={isOpen} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
        >
          <Calendar className="size-3.5 text-muted-foreground" />
          <span>{displayText}</span>
          <ChevronDown className="size-3.5 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align={align === 'left' ? 'start' : 'end'}
        sideOffset={8}
        className="flex w-auto flex-col p-0"
      >
        <div className="flex">
          <div className="border-r border-border p-2 min-w-[140px] flex flex-col">
            <div className="text-[12px] font-semibold text-muted-foreground px-2 pb-1">Quick select</div>
            {DEFAULT_PRESETS.map((preset) => (
              <button
                key={preset.text}
                type="button"
                onClick={() => onPresetClick(preset)}
                className="text-left text-[13px] text-primary bg-transparent border-0 px-2 py-1 rounded hover:bg-muted"
              >
                {preset.text}
              </button>
            ))}
          </div>
          <div className="p-2">
            <DayPicker
              mode="range"
              selected={tempRange}
              onSelect={(next) => setTempRange(next || { from: null, to: null })}
              numberOfMonths={2}
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 p-2 border-t border-border">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={onCancel}
          >Cancel</Button>
          <Button
            type="button"
            size="sm"
            onClick={onApply}
            disabled={!tempRange.from || !tempRange.to}
          >Apply</Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}

export default DateRangePicker
